// Powder smoke for the profile ship's broadside, drawn on a canvas: every gun
// throws a short, bright muzzle flash and a dense ball of white-grey smoke
// that billows out, slows, swells and rises while the wind carries it off,
// so a broadside leaves a thick bank hanging over the ship that only thins
// out after ten seconds or more. Soft, irregular smoke sprites are generated
// once; the loop only runs while there is smoke to draw.

export interface SmokeShot {
  /** Canvas position of the gun port, in CSS pixels. */
  x: number;
  y: number;
  /** Seconds after the volley starts. */
  delay: number;
}

interface Particle {
  kind: 'smoke' | 'soot' | 'flash' | 'spark' | 'flame';
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  grow: number;
  age: number;
  life: number;
  peak: number;
  spin: number;
  angle: number;
  sprite: number;
  /** Phase for the swirling currents a fire's heat stirs up (heat smoke only). */
  seed?: number;
}

/** The swirl of hot air above a fire: a smooth, ever-changing push, px/s², from a few crossed waves. */
function swirl(x: number, y: number, t: number, seed: number): [number, number] {
  const ax = Math.sin(y * 0.035 + t * 1.7 + seed) + 0.6 * Math.sin(y * 0.09 - t * 2.3 + seed * 1.7);
  const ay = Math.cos(x * 0.04 - t * 1.4 + seed * 0.7) + 0.5 * Math.sin(x * 0.11 + t * 2.9 + seed);
  return [ax, ay];
}

const SPRITES = 6;
/** Wind and buoyancy, CSS px/s: the smoke drifts downwind (towards the bow, left) and rises. */
const WIND = -11;
const RISE = 16;

/** Soft, irregular puffs: light powder smoke, or (`dark`) the black smoke of burning wood and tar. */
function makeSprites(dark = false): HTMLCanvasElement[] {
  const sprites: HTMLCanvasElement[] = [];
  for (let n = 0; n < SPRITES; n++) {
    const size = 128;
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const g = c.getContext('2d')!;
    // a lumpy puff: many soft blobs of slightly different greys, densest in the middle
    for (let i = 0; i < 26; i++) {
      const r = size * (0.12 + Math.random() * 0.2);
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * size * 0.22;
      const x = size / 2 + Math.cos(angle) * dist;
      const y = size / 2 + Math.sin(angle) * dist;
      const shade = dark ? 38 + Math.floor(Math.random() * 40) : 200 + Math.floor(Math.random() * 45);
      const blob = g.createRadialGradient(x, y, 0, x, y, r);
      blob.addColorStop(0, `rgba(${shade},${shade - 4},${shade - 12},0.55)`);
      blob.addColorStop(0.6, `rgba(${shade},${shade - 4},${shade - 12},0.22)`);
      blob.addColorStop(1, `rgba(${shade},${shade - 4},${shade - 12},0)`);
      g.fillStyle = blob;
      g.beginPath();
      g.arc(x, y, r, 0, Math.PI * 2);
      g.fill();
    }
    // a darker, denser core gives the puff body
    const core = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size * 0.32);
    core.addColorStop(0, dark ? 'rgba(22,20,18,0.45)' : 'rgba(150,145,136,0.35)');
    core.addColorStop(1, dark ? 'rgba(22,20,18,0)' : 'rgba(150,145,136,0)');
    g.fillStyle = core;
    g.fillRect(0, 0, size, size);
    sprites.push(c);
  }
  return sprites;
}

const rand = (a: number, b: number) => a + Math.random() * (b - a);

export class SmokeLayer {
  private readonly canvas: HTMLCanvasElement;
  private readonly g: CanvasRenderingContext2D;
  private sprites: HTMLCanvasElement[] | null = null;
  private soot: HTMLCanvasElement[] | null = null;
  /** A fire burning on the ship: where it is now (it moves as the ship sinks), until when, and since when. */
  private burner: {
    source: () => { x: number; y: number; waterline: number } | null;
    scale: number;
    from: number;
    until: number;
    smokeCarry: number;
    fireCarry: number;
  } | null = null;
  /** Nothing of the fire is drawn below this line (canvas px): it is under water. */
  private waterline = Infinity;
  private particles: Particle[] = [];
  private pending: { at: number; shot: SmokeShot; scale: number }[] = [];
  private frame = 0;
  private last = 0;
  private clock = 0;

  constructor(canvas: HTMLCanvasElement, private readonly still = false) {
    this.canvas = canvas;
    this.g = canvas.getContext('2d')!;
  }

  /** Fires a volley: `scale` is the ship's drawn size (1 = a frigate). */
  fire(shots: readonly SmokeShot[], scale: number): void {
    this.sprites ??= makeSprites();
    this.fit();
    for (const shot of shots) this.pending.push({ at: this.clock + shot.delay, shot, scale });
    if (!this.frame) {
      this.last = 0;
      this.frame = requestAnimationFrame((t) => this.step(t));
    }
  }

  /**
   * Sets the ship on fire at `source` (asked every frame, so the fire goes
   * down with the ship; null once it is under) for `seconds`: dark smoke
   * that starts as a wisp and thickens, rising and drifting off, with
   * flames flickering at its foot.
   */
  burn(source: () => { x: number; y: number; waterline: number } | null, scale: number, seconds: number): void {
    this.sprites ??= makeSprites();
    this.soot ??= makeSprites(true);
    this.fit();
    this.burner = { source, scale, from: this.clock, until: this.clock + seconds, smokeCarry: 0, fireCarry: 0 };
    this.waterline = Infinity;
    if (!this.frame) {
      this.last = 0;
      this.frame = requestAnimationFrame((t) => this.step(t));
    }
  }

  stop(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.particles = [];
    this.pending = [];
    this.burner = null;
  }

  private fit(): void {
    // smoke is soft: normal resolution is plenty, and keeps a large canvas light
    const dpr = 1;
    const r = this.canvas.getBoundingClientRect();
    const w = Math.round(r.width * dpr);
    const h = Math.round(r.height * dpr);
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.g.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private emit(shot: SmokeShot, s: number): void {
    const { x, y } = shot;
    // the flash: a hot glow and a spray of sparks, gone in a blink
    this.particles.push({ kind: 'flash', x, y, vx: 0, vy: 0, size: 34 * s, grow: 60 * s, age: 0, life: 0.12, peak: 1, spin: 0, angle: 0, sprite: 0 });
    for (let i = 0; i < 7; i++) {
      const a = rand(0, Math.PI * 2);
      const v = rand(120, 300) * s;
      this.particles.push({ kind: 'spark', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v * 0.6, size: rand(2, 4) * s, grow: 0, age: 0, life: rand(0.08, 0.22), peak: 1, spin: 0, angle: 0, sprite: 0 });
    }
    // the smoke: a dense ball thrown out of the port, mostly sideways and a little down,
    // then left to slow, swell, rise and drift
    // the body: a dense ball thrown out of the port, then left to slow, swell
    // and climb — hot powder smoke rises past the bulwarks into the rigging
    const count = this.still ? 10 : 26;
    for (let i = 0; i < count; i++) {
      const a = rand(0, Math.PI * 2);
      const v = this.still ? 0 : rand(30, 190) * s;
      this.particles.push({
        kind: 'smoke',
        x: x + rand(-4, 4) * s,
        y: y + rand(-3, 3) * s,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v * 0.35 - (this.still ? 0 : rand(15, 55) * s),
        size: rand(12, 26) * s,
        grow: rand(35, 80) * s,
        age: -rand(0, 0.12),
        life: this.still ? rand(2, 3) : rand(8, 15),
        peak: rand(0.35, 0.6),
        spin: rand(-0.25, 0.25),
        angle: rand(0, Math.PI * 2),
        sprite: Math.floor(rand(0, SPRITES)),
      });
    }
    // the fringe: thinner, smaller wisps flung further, so the bank frays at its edges
    if (!this.still) {
      for (let i = 0; i < 14; i++) {
        const a = rand(0, Math.PI * 2);
        const v = rand(140, 320) * s;
        this.particles.push({
          kind: 'smoke',
          x,
          y,
          vx: Math.cos(a) * v,
          vy: Math.sin(a) * v * 0.45 - rand(20, 70) * s,
          size: rand(6, 14) * s,
          grow: rand(25, 55) * s,
          age: -rand(0, 0.08),
          life: rand(4, 9),
          peak: rand(0.15, 0.32),
          spin: rand(-0.6, 0.6),
          angle: rand(0, Math.PI * 2),
          sprite: Math.floor(rand(0, SPRITES)),
        });
      }
    }
  }

  private feedFire(dt: number): void {
    const b = this.burner;
    if (!b) return;
    if (this.clock > b.until) {
      this.burner = null;
      return;
    }
    const at = b.source();
    if (!at) {
      // the stern is under: the fire is out, the smoke in the air drifts on
      this.burner = null;
      return;
    }
    this.waterline = at.waterline;
    const s = b.scale;
    const age = this.clock - b.from;
    const takeHold = Math.min(1, 0.25 + age / 2.2);
    // heat smoke: many loose, thin puffs, from a wisp to a billowing plume
    b.smokeCarry += (this.still ? 4 : 10 + takeHold * 45) * dt;
    while (b.smokeCarry >= 1) {
      b.smokeCarry -= 1;
      this.particles.push({
        kind: 'soot',
        x: at.x + rand(-12, 12) * s,
        y: at.y - rand(4, 14) * s,
        vx: rand(-14, 14) * s,
        vy: -rand(35, 70) * s,
        size: rand(6, 12) * s,
        grow: rand(28, 60) * s,
        age: 0,
        life: this.still ? rand(2, 3) : rand(4, 8),
        peak: rand(0.16, 0.34) * takeHold,
        spin: rand(-0.6, 0.6),
        angle: rand(0, Math.PI * 2),
        sprite: Math.floor(rand(0, SPRITES)),
        seed: rand(0, 100),
      });
    }
    if (this.still) return;
    // flames: a crowd of small tongues, white-hot at the root, reddening as they rise and die
    b.fireCarry += (35 + takeHold * 70) * dt;
    while (b.fireCarry >= 1) {
      b.fireCarry -= 1;
      const spread = rand(-1, 1);
      this.particles.push({
        kind: 'flame',
        x: at.x + spread * 13 * s,
        y: at.y + rand(-2, 2) * s,
        vx: -spread * 6 * s + rand(-4, 4) * s,
        vy: -rand(45, 95) * s * (1 - Math.abs(spread) * 0.45),
        size: rand(3.5, 7.5) * s * takeHold,
        grow: -rand(2, 5) * s,
        age: 0,
        life: rand(0.22, 0.55),
        peak: rand(0.5, 0.85),
        spin: 0,
        angle: 0,
        sprite: 0,
        seed: rand(0, 100),
      });
    }
  }

  private step(now: number): void {
    const dt = this.last ? Math.min(0.05, (now - this.last) / 1000) : 1 / 60;
    this.last = now;
    this.clock += dt;
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const p = this.pending[i]!;
      if (p.at <= this.clock) {
        this.emit(p.shot, p.scale);
        this.pending.splice(i, 1);
      }
    }
    this.feedFire(dt);

    const g = this.g;
    const w = this.canvas.width;
    const h = this.canvas.height;
    g.save();
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, w, h);
    g.restore();

    const alive: Particle[] = [];
    // smoke first, then the flashes on top
    for (const pass of ['smoke', 'light'] as const) {
      g.globalCompositeOperation = pass === 'light' ? 'lighter' : 'source-over';
      if (pass === 'light') {
        // fire never shows below the waterline: under water it is out
        g.save();
        g.beginPath();
        g.rect(-10000, -10000, 20000, this.waterline + 10000);
        g.clip();
      }
      for (const p of this.particles) {
        const isLight = p.kind === 'flash' || p.kind === 'spark' || p.kind === 'flame';
        if ((pass === 'light') !== isLight) continue;
        p.age += dt;
        if (p.age > p.life) continue;
        if (pass === 'smoke') alive.push(p);
        else alive.push(p);
        if (p.age < 0) continue;
        const t = p.age / p.life;
        if (p.kind === 'smoke' || p.kind === 'soot') {
          const drag = Math.exp(-2.6 * dt);
          if (p.kind === 'soot' && !this.still) {
            // heat smoke: buoyant, churned by swirling currents, sheared off by the wind as it climbs
            const [ax, ay] = swirl(p.x, p.y, this.clock, p.seed ?? 0);
            const lift = Math.max(0.2, 1 - t);
            p.vx = p.vx * Math.exp(-1.2 * dt) + (ax * 55 + WIND * (1.4 + 2.2 * t)) * dt;
            p.vy = p.vy * Math.exp(-0.9 * dt) + (ay * 35 - RISE * 4.5 * lift) * dt;
          } else {
            p.vx = p.vx * drag + (this.still ? 0 : WIND * dt);
            p.vy = p.vy * drag - (this.still ? 0 : RISE * dt);
          }
          p.x += p.vx * dt;
          p.y += p.vy * dt;
          // swells fast while the blast is fresh, then keeps spreading slowly
          p.size += p.grow * dt / (1 + p.age * 1.4);
          p.angle += p.spin * dt;
          const alpha = p.peak * Math.min(1, p.age / (p.kind === 'soot' ? 0.35 : 0.08)) * Math.pow(1 - t, 1.6);
          // heat smoke starts black and thins to grey: the dark puffs give way to light ones as it ages
          const sprite = (p.kind === 'soot' && t < 0.55 ? this.soot! : this.sprites!)[p.sprite]!;
          g.globalAlpha = alpha;
          g.save();
          g.translate(p.x, p.y);
          g.rotate(p.angle);
          g.drawImage(sprite, -p.size, -p.size, p.size * 2, p.size * 2);
          g.restore();
        } else {
          p.x += p.vx * dt;
          p.y += p.vy * dt;
          p.size += p.grow * dt;
          const alpha = p.peak * (1 - t);
          const r = Math.max(1, p.size);
          const glow = g.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
          if (p.kind === 'flame') {
            // white-yellow at the root, orange, then a dull red as the tongue rises and dies
            const flicker = 0.75 + 0.25 * Math.sin(this.clock * 31 + (p.seed ?? 0));
            const a = alpha * flicker;
            const [r0, g0, b0] = t < 0.3 ? [255, 236, 170] : t < 0.6 ? [255, 160, 55] : [190, 60, 20];
            glow.addColorStop(0, `rgba(${r0},${g0},${b0},${a})`);
            glow.addColorStop(0.55, `rgba(${r0},${Math.round(g0 * 0.6)},${Math.round(b0 * 0.4)},${a * 0.45})`);
            glow.addColorStop(1, 'rgba(120,30,5,0)');
          } else if (p.kind === 'flash') {
            glow.addColorStop(0, `rgba(255,248,215,${alpha})`);
            glow.addColorStop(0.35, `rgba(255,190,80,${alpha * 0.85})`);
            glow.addColorStop(1, 'rgba(255,110,30,0)');
          } else {
            glow.addColorStop(0, `rgba(255,236,170,${alpha})`);
            glow.addColorStop(1, 'rgba(255,150,50,0)');
          }
          g.globalAlpha = 1;
          g.fillStyle = glow;
          g.beginPath();
          g.arc(p.x, p.y, r, 0, Math.PI * 2);
          g.fill();
        }
      }
      if (pass === 'light') g.restore();
    }
    g.globalAlpha = 1;
    g.globalCompositeOperation = 'source-over';
    this.particles = alive;

    if (this.particles.length || this.pending.length || this.burner) {
      this.frame = requestAnimationFrame((t) => this.step(t));
    } else {
      this.frame = 0;
      g.save();
      g.setTransform(1, 0, 0, 1, 0, 0);
      g.clearRect(0, 0, w, h);
      g.restore();
    }
  }
}
